// WHO ICD API Service
// Integration with WHO International Classification of Diseases API
// Part of Diagnosis Nomenclature Implementation

/**
 * WHO ICD API Service
 * 
 * Provides integration with WHO ICD-10/ICD-11 API for diagnosis code lookup
 * Uses hybrid approach: cache common codes locally, fetch from API when needed
 */
class WHOICDApiService {
  constructor() {
    // WHO ICD API base URL (ICD-11)
    // Note: WHO API primarily supports ICD-11, but we can search for ICD-10 equivalents
    this.apiBaseUrl = 'https://id.who.int/icd/release/11/2024-05';
    
    // API key from environment variable
    this.apiKey = process.env.REACT_APP_WHO_ICD_API_KEY || null;
    
    // In-memory cache for API responses
    this.cache = new Map();
    
    // Cache expiration (5 minutes)
    this.cacheExpiration = 5 * 60 * 1000;
  }

  /**
   * Check if API is available
   */
  isApiAvailable() {
    return this.apiKey !== null && this.apiKey !== undefined && this.apiKey !== '';
  }

  /**
   * Search for ICD codes by term
   * 
   * @param {string} searchTerm - Search term (e.g., "pneumonia", "J18.9")
   * @param {string} linearization - Linearization type (default: 'mms')
   * @returns {Promise<Object>} Search results
   */
  async searchCodes(searchTerm, linearization = 'mms') {
    try {
      if (!this.isApiAvailable()) {
        console.warn('WHO ICD API key not configured, skipping API search');
        return { destinationEntities: [], error: 'API not configured' };
      }

      // Check cache first
      const cacheKey = `search:${searchTerm}:${linearization}`;
      const cached = this.cache.get(cacheKey);
      if (cached && (Date.now() - cached.timestamp) < this.cacheExpiration) {
        return cached.data;
      }

      // Fetch from WHO API
      const url = `${this.apiBaseUrl}/mms/release/${linearization}/search?q=${encodeURIComponent(searchTerm)}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'API-Version': 'v2',
          'Accept-Language': 'en',
          'Accept': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          console.error('WHO ICD API authentication failed - check API key');
          return { destinationEntities: [], error: 'Authentication failed' };
        }
        if (response.status === 429) {
          console.warn('WHO ICD API rate limit exceeded');
          return { destinationEntities: [], error: 'Rate limit exceeded' };
        }
        throw new Error(`WHO ICD API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      // Cache result
      this.cache.set(cacheKey, {
        data: data,
        timestamp: Date.now()
      });
      
      return data;
    } catch (error) {
      console.error('WHO ICD API search error:', error);
      // Return empty result instead of throwing
      return { destinationEntities: [], error: error.message };
    }
  }

  /**
   * Get code details by ICD-10 code
   * 
   * @param {string} icd10Code - ICD-10 code (e.g., "J18.9")
   * @returns {Promise<Object|null>} Code details or null
   */
  async getCodeDetails(icd10Code) {
    try {
      if (!this.isApiAvailable()) {
        console.warn('WHO ICD API key not configured, skipping code lookup');
        return null;
      }

      // Check cache first
      const cacheKey = `code:${icd10Code}`;
      const cached = this.cache.get(cacheKey);
      if (cached && (Date.now() - cached.timestamp) < this.cacheExpiration) {
        return cached.data;
      }

      // WHO API primarily supports ICD-11, but we can search for ICD-10 equivalents
      // Search for the code to find ICD-11 equivalent
      const searchResults = await this.searchCodes(icd10Code);
      
      if (searchResults.destinationEntities && searchResults.destinationEntities.length > 0) {
        // Get the first matching entity
        const entity = searchResults.destinationEntities[0];
        
        // Try to get full entity details
        // Note: WHO API uses @id and @value properties (JSON-LD format)
        const entityId = entity['@id'] || entity.id || null;
        if (entityId) {
          const entityUrl = entityId;
          const response = await fetch(entityUrl, {
            method: 'GET',
            headers: {
              'API-Version': 'v2',
              'Accept-Language': 'en',
              'Accept': 'application/json',
              'Authorization': `Bearer ${this.apiKey}`
            }
          });

          if (response.ok) {
            const entityData = await response.json();
            
            // Cache result
            this.cache.set(cacheKey, {
              data: entityData,
              timestamp: Date.now()
            });
            
            return entityData;
          }
        }
        
        // Cache search result if entity details unavailable
        this.cache.set(cacheKey, {
          data: entity,
          timestamp: Date.now()
        });
        
        return entity;
      }

      return null;
    } catch (error) {
      console.error('WHO ICD API get code error:', error);
      return null;
    }
  }

  /**
   * Validate ICD-10 code
   * 
   * @param {string} icd10Code - ICD-10 code to validate
   * @returns {Promise<boolean>} True if code is valid
   */
  async validateCode(icd10Code) {
    try {
      if (!this.isApiAvailable()) {
        // If API not available, do basic format validation
        // ICD-10 format: Letter followed by numbers (e.g., J18.9, N39.0)
        const icd10Pattern = /^[A-Z][0-9]{2}\.[0-9]$/;
        return icd10Pattern.test(icd10Code);
      }

      const codeDetails = await this.getCodeDetails(icd10Code);
      return codeDetails !== null && codeDetails !== undefined;
    } catch (error) {
      console.error('WHO ICD API validation error:', error);
      // Fallback to format validation
      const icd10Pattern = /^[A-Z][0-9]{2}\.[0-9]$/;
      return icd10Pattern.test(icd10Code);
    }
  }

  /**
   * Sync diagnosis with WHO API
   * 
   * @param {string} icd10Code - ICD-10 code to sync
   * @returns {Promise<Object|null>} WHO API data or null
   */
  async syncDiagnosis(icd10Code) {
    try {
      if (!this.isApiAvailable()) {
        return null;
      }

      const codeDetails = await this.getCodeDetails(icd10Code);
      
      if (!codeDetails) {
        return null;
      }

      // Extract relevant data from WHO API response
      // Note: WHO API uses @value for localized strings (JSON-LD format)
      const title = codeDetails.title?.['@value'] || codeDetails.title || codeDetails.label || '';
      const definition = codeDetails.definition?.['@value'] || codeDetails.definition || codeDetails.description || '';
      
      return {
        code: codeDetails.code || codeDetails['@id'] || icd10Code,
        title: title,
        definition: definition,
        parent: codeDetails.parent || codeDetails.parentCode || null,
        children: codeDetails.children || codeDetails.childCodes || []
      };
    } catch (error) {
      console.error('WHO ICD API sync error:', error);
      return null;
    }
  }

  /**
   * Clear cache
   */
  clearCache() {
    this.cache.clear();
  }

  /**
   * Get cache statistics
   */
  getCacheStats() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }
}

export default new WHOICDApiService();

